import PropTypes from 'prop-types';
import { Grid, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Box, Chip } from '@mui/material';
import {
    AssignmentTurnedInOutlined,
    EventOutlined,
    VerifiedUserOutlined,
    CheckCircleOutlined,
    CancelOutlined,
    HourglassEmptyOutlined
} from '@mui/icons-material';
import SectionHeader from '../SectionHeader';

const EmploymentDetailsSection = ({ form, handleChange, handleBlur, errors, touched }) => {
    return (
        <>
            <SectionHeader
                icon={AssignmentTurnedInOutlined}
                title="Employment Details"
                subtitle="Offer, joining dates, verification and training status"
            />
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Date of Offer */}
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label="Date of Offer"
                        type="date"
                        fullWidth
                        value={form.dateOfOffer}
                        onChange={handleChange('dateOfOffer')}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EventOutlined fontSize="small" color="action" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>

                {/* Date of Joining */}
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label="Date of Joining"
                        type="date"
                        fullWidth
                        value={form.dateOfJoining}
                        onChange={handleChange('dateOfJoining')}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            min: form.dateOfOffer || undefined
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EventOutlined fontSize="small" color="action" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>

                {/* Effective Employment Date */}
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label="Effective Employment Date"
                        type="date"
                        fullWidth
                        value={form.effectiveDate}
                        onChange={handleChange('effectiveDate')}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            min: form.dateOfJoining || undefined
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EventOutlined fontSize="small" color="action" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>

                {/* BGV Status */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={touched.bgvStatus && !!errors.bgvStatus}>
                        <InputLabel>BGV Status</InputLabel>
                        <Select
                            value={form.bgvStatus}
                            label="BGV Status"
                            onChange={handleChange('bgvStatus')}
                            onBlur={handleBlur('bgvStatus')}
                            startAdornment={
                                <Box sx={{ mr: 1, display: 'flex' }}>
                                    <VerifiedUserOutlined
                                        fontSize="small"
                                        color={touched.bgvStatus && errors.bgvStatus ? 'error' : 'action'}
                                    />
                                </Box>
                            }
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value="Verified">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleOutlined sx={{ color: 'success.main', fontSize: 18 }} />
                                    Verified
                                </Box>
                            </MenuItem>
                            <MenuItem value="Not Verified">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CancelOutlined sx={{ color: 'error.main', fontSize: 18 }} />
                                    Not Verified
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Training Status */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={touched.trainingStatus && !!errors.trainingStatus}>
                        <InputLabel>Training Status</InputLabel>
                        <Select
                            value={form.trainingStatus}
                            label="Training Status"
                            onChange={handleChange('trainingStatus')}
                            onBlur={handleBlur('trainingStatus')}
                            startAdornment={
                                <Box sx={{ mr: 1, display: 'flex' }}>
                                    <AssignmentTurnedInOutlined
                                        fontSize="small"
                                        color={touched.trainingStatus && errors.trainingStatus ? 'error' : 'action'}
                                    />
                                </Box>
                            }
                            sx={{ borderRadius: 2 }}
                        >
                            <MenuItem value="Completed">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label="Completed"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        icon={<CheckCircleOutlined />}
                                        sx={{ height: 24 }}
                                    />
                                </Box>
                            </MenuItem>
                            <MenuItem value="Ongoing">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label="Ongoing"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        icon={<HourglassEmptyOutlined />}
                                        sx={{ height: 24 }}
                                    />
                                </Box>
                            </MenuItem>
                            <MenuItem value="Not Completed">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label="Not Completed"
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        icon={<CancelOutlined />}
                                        sx={{ height: 24 }}
                                    />
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </>
    );
};

EmploymentDetailsSection.propTypes = {
    form: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired
};

export default EmploymentDetailsSection;
